// pdfBrand.js (P4-CC10) — shared branded header/footer for the NEW document PDFs
// (statements + credit notes). Replicates the invoice PDF's look so all three read
// as one branded system, WITHOUT touching invoicePdf.js (the invoice output stays
// byte-identical). jsPDF is already a dependency.
import { LOGO_PNG_DATAURI } from "./logoData";

export const COLORS = {
  NAVY: [15, 23, 42], BLUE: [59, 130, 246], GREY: [100, 116, 139],
  LIGHT: [241, 245, 249], MUTE: [148, 163, 184], DANGER: [192, 57, 43],
};
export const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;   // €

// jsPDF's standard Helvetica renders in WinAnsi (Latin1) — non-Latin1 glyphs (en/em
// dashes, arrows, smart quotes, ellipsis, minus, bullets) mangle. Map them to ASCII.
// Keep € (WinAnsi 0x80), middle-dot (0xB7) and ² (0xB2) — those render fine. Handles
// arrays too (for splitTextToSize input). Route EVERY doc.text arg + autotable cell
// through this. (Literal glyphs in the ranges below compile fine — the WinAnsi limit
// is only in jsPDF's PDF font, not in the JS source.)
export function asciiPdf(v) {
  if (Array.isArray(v)) return v.map(asciiPdf);
  return String(v == null ? "" : v)
    .replace(/[‐‑‒–—―−]/g, "-")   // hyphens / en / em dashes / minus
    .replace(/[→↦➔➙➜➡⟶]/g, " to ") // arrows
    .replace(/[‘’‚‛]/g, "'")                      // single smart quotes
    .replace(/[“”„‟]/g, '"')                      // double smart quotes
    .replace(/[•‣◦⁃]/g, "-")                      // bullets
    .replace(/…/g, "...")                                        // ellipsis
    .replace(/[♻︎️]/g, "")                             // recycle / variation selectors
    .replace(/ /g, " ");                                         // non-breaking space
}

/** Normalise a raw Business Settings record into the header/footer fields. */
export function businessBrand(settings) {
  const b = settings || {};
  const addressLines = [b.address_line1, b.address_line2, [b.city, b.county].filter(Boolean).join(", "), b.postal_code, b.country].filter(Boolean);
  return {
    // ⛔ SEND-0b (26 Oct 2026) — THIS MUST NOT FALL BACK TO "Msquared".
    // It used to read `|| "Msquared"`, and statementPdf.js and creditNotePdf.js
    // both normalise through here, so a tenant with no trading_name and no
    // legal_name printed OUR name in the header and footer of their customer
    // statements and their credit notes. It is reached two ways, not one: a
    // tenant who has not set a name, and a tenant whose settings failed to load
    // — CreditNoteView does `getBusinessSettings().catch(() => ({}))` and
    // StatementView defaults `business = {}`, and both hand this an empty object.
    // ⛔ Do not reinstate a default here. brandedHeader draws an empty string
    // harmlessly and brandedFooter filters it out; a tenant's document carrying
    // another company's name is the thing that is not harmless.
    name: b.trading_name || b.legal_name || "",
    tagline: b.website || "",
    addressLines,
    email: b.email || "",
    phone: b.phone || "",
    vat: b.vat_number || "",
    logo_url: b.logo_url || "",
    bank_account_name: b.bank_account_name || "",
    bank_name: b.bank_name || "",
    iban: b.iban || "",
    bic: b.bic || "",
  };
}

// jsPDF image format from a data-URI prefix (logo_url is a base64 data URI).
function imgFormat(dataUri) {
  const m = /^data:image\/(png|jpe?g|webp)/i.exec(String(dataUri || ""));
  if (!m) return null;
  const t = m[1].toLowerCase();
  return t === "png" ? "PNG" : (t === "jpg" || t === "jpeg") ? "JPEG" : t === "webp" ? "WEBP" : null;
}

/**
 * Draw the navy branded header band (logo + name/tagline + right-aligned title/number).
 * Logo: tenant logo_url -> bundled monogram -> "M2" lettermark. Returns the y after the band.
 */
export function brandedHeader(doc, { title, docNumber, business }) {
  const B = business;
  const pageW = doc.internal.pageSize.getWidth();
  const M = 14, rightX = pageW - M;

  doc.setFillColor(...COLORS.NAVY);
  doc.rect(0, 0, pageW, 32, "F");

  let drew = false;
  if (B.logo_url) { try { doc.addImage(B.logo_url, imgFormat(B.logo_url) || "PNG", M, 9, 14, 14); drew = true; } catch { drew = false; } }
  if (!drew) { try { doc.addImage(LOGO_PNG_DATAURI, "PNG", M, 9, 14, 14); drew = true; } catch { drew = false; } }
  if (!drew) {
    doc.setFillColor(...COLORS.BLUE);
    doc.roundedRect(M, 9, 14, 14, 2.5, 2.5, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text(asciiPdf("M²"), M + 7, 18.5, { align: "center" });   // M²
  }

  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(18);
  doc.text(asciiPdf(B.name), M + 19, 16);
  if (B.tagline) { doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...COLORS.MUTE); doc.text(asciiPdf(B.tagline), M + 19, 22); }

  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.text(asciiPdf(title), rightX, 16, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...COLORS.MUTE);
  doc.text(asciiPdf(docNumber || "-"), rightX, 23, { align: "right" });
  return 42;
}

/** Draw the per-page footer band (Name · email · VAT + page numbers). */
export function brandedFooter(doc, business) {
  const B = business;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 14, rightX = pageW - M;
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFillColor(...COLORS.LIGHT);
    doc.rect(0, pageH - 12, pageW, 12, "F");
    doc.setFont("helvetica", "normal"); doc.setTextColor(...COLORS.GREY); doc.setFontSize(7.5);
    doc.text(asciiPdf([B.name, B.email, B.vat ? `VAT ${B.vat}` : null].filter(Boolean).join(" · ")), M, pageH - 5);   // ·
    doc.text(asciiPdf(`Page ${p} of ${pageCount}`), rightX, pageH - 5, { align: "right" });
  }
}
